import {
  Scene,
  Color3,
  HDRCubeTexture,
  CubeTexture,
  PBRMaterial,
  Texture,
  Vector2,
  Mesh,
  AbstractMesh,
} from 'babylonjs'
import { Context3D } from '../3d-context'
import { EnvType, getTypeByFileName } from '../environment'
import { Layer } from 'babylonjs/Layers/layer'
import _uniqBy from 'lodash/uniqBy'
import {
  IColor,
  IMaterialStyle,
  IMesh,
  IModel,
  ITexture,
  IVariant,
} from '../../type/modelType'
import { getModelMaterial } from '../modelEngine/loadModel'
import { GLTFType } from '../../type/GLTFType'
import { convertBlobToBase64 } from '../utils'
export interface customMaterialAttribute {
  roughness?: number
  metallic?: number
  color?: string
  alpha?: number
}

export function preloadTexture(textureAssets: ITexture[]) {
  return new Promise((resolve, reject) => {
    console.log('preloadTexture START')
    const preloadPromise: any = []
    textureAssets.forEach(textureAsset => {
      preloadPromise.push(createTexture(textureAsset.source.texture))

      if (textureAsset.source.normal) {
        preloadPromise.push(createTexture(textureAsset.source.normal))
      }
    })

    Promise.all(preloadPromise).then(values => {
      console.log('preloadTexture DONE')
      resolve(true)
    })
  })
}

export function preloadHDR(filename: string, context = Context3D.get()) {
  if (!context.scene) return

  new HDRCubeTexture(
    './assets/env/' + filename + '.hdr',
    context.scene,
    24,
    false,
    true,
    false,
    true
  )
}

export async function changeTexture(
  textureAsset: ITexture,
  materialsName: string[],
  customMaterialAttribute?: customMaterialAttribute,
  context = Context3D.get()
) {
  if (!context.scene) return

  let texture: Texture | undefined
  if (textureAsset.source.normal) {
    texture = await createTexture(
      textureAsset.source.texture,
      textureAsset.repeat
    )
  }

  let normalMapTexture: Texture | undefined
  if (textureAsset.source.normal) {
    normalMapTexture = await createTexture(
      textureAsset.source.normal,
      textureAsset.repeat
    )
  }

  materialsName.forEach(materialName => {
    const material = context.scene?.getMaterialByID(materialName) as PBRMaterial
    if (!material) return

    material.albedoTexture?.dispose()
    material.bumpTexture?.dispose()
    material.metallicTexture?.dispose()
    // @ts-ignore
    material.albedoTexture = null
    // @ts-ignore
    material.bumpTexture = null
    // @ts-ignore
    material.metallicTexture = null

    material.albedoColor = new Color3(1, 1, 1) // Clear to white color before apply texture, if not color will overlap texture.
    if (texture) material.albedoTexture = texture
    material.roughness = textureAsset?.roughness || 0.5
    material.metallic = textureAsset?.metallic || 1

    if (normalMapTexture) {
      material.bumpTexture = normalMapTexture
      material.bumpTexture.level = 0.5
    }
  })
}

/**
 * Create texture by url and set props
 */
function createTexture(
  textureUrl: string,
  repeat?: number,
  context = Context3D.get()
) {
  return new Promise<Texture>((resolve, reject) => {
    if (!context.scene) {
      reject(false)
      return
    }

    // context.engine?.displayLoadingUI()
    // customLoadingUI(true)

    fetch(textureUrl, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    })
      .then(response => response.blob())
      .then(blob => {
        convertBlobToBase64(blob).then(base64 => {
          const texture: Texture = new Texture(base64, context.scene as Scene)

          texture.onLoadObservable.add(t => {
            t.uScale = repeat ? repeat : 1 // repeat
            t.vScale = repeat ? repeat : 1 // repeat

            resolve(texture)
          })
        })
      })
      .catch(e => {
        reject(false)
      })
  })
}

export function changeColor(
  color: string,
  materialsName: string[],
  customMaterialAttribute?: customMaterialAttribute,
  overlay?: boolean,
  context = Context3D.get()
) {
  if (!context.scene) return

  materialsName.forEach(materialName => {
    const material = context.scene?.getMaterialByID(materialName) as PBRMaterial
    if (!material) return

    console.log('changeColor', color, Color3.FromHexString(color))

    material.albedoColor = Color3.FromHexString(color)

    updateCustomMaterialAttribute(material, customMaterialAttribute)

    if (!overlay) {
      material.albedoTexture?.dispose()
      material.bumpTexture?.dispose()
      material.metallicTexture?.dispose()
      // @ts-ignore
      material.albedoTexture = null
      // @ts-ignore
      material.bumpTexture = null
      // @ts-ignore
      material.metallicTexture = null
    } else {
      material.albedoTexture?.dispose()
      material.metallicTexture?.dispose()
      // @ts-ignore
      material.albedoTexture = null
      // @ts-ignore
      material.metallicTexture = null
    }
  })
}

export function updateCustomMaterialAttribute(
  material: PBRMaterial,
  customMaterialAttribute?: customMaterialAttribute
) {
  if (customMaterialAttribute?.alpha)
    material.alpha = customMaterialAttribute?.alpha
  if (customMaterialAttribute?.metallic)
    material.metallic = customMaterialAttribute?.metallic
  if (customMaterialAttribute?.roughness)
    material.roughness = customMaterialAttribute?.roughness
}

export function toggleMesh(
  meshesName: string[],
  option?: boolean,
  context = Context3D.get()
) {
  if (!context.scene) return

  meshesName.forEach(meshName => {
    const mesh = context.scene?.getMeshByID(meshName)
    if (!mesh) return

    if (option !== undefined) {
      mesh.setEnabled(option)
    } else {
      mesh.setEnabled(!mesh.isEnabled())
    }
  })
}

export function changeTransparentLevel(
  alpha: number,
  materialsName: string[],
  context = Context3D.get()
) {
  if (!context.scene) return

  materialsName.forEach(materialName => {
    const material = context.scene?.getMaterialByID(materialName) as PBRMaterial
    if (!material) return

    material.alpha = alpha
  })
}

export function toggleSkyBox(context = Context3D.get(), enable?: boolean) {
  if (!context.scene) return

  const scene = context.scene as Scene

  console.log('enable', enable)

  const backgroundHdrSkybox = scene.getMeshByName('hdrSkyBox') as Mesh
  const skybox_mesh = scene.getMeshByName('skybox_mesh')
  const floor_mesh = scene.getMeshByName('floor_mesh')

  const bgImage = scene.layers?.find(r => r.name === 'BackgroudImage') as Layer

  console.log('backgroundHdrSkybox', backgroundHdrSkybox)

  if (
    enable === false ||
    (!enable &&
      (backgroundHdrSkybox?.isEnabled() || (bgImage && bgImage.scale?.x > 0)))
  ) {
    console.log('to disable', backgroundHdrSkybox, bgImage)
    if (backgroundHdrSkybox) {
      backgroundHdrSkybox.setEnabled(false)
      if (scene.environmentTexture) {
        // context.scene.environmentTexture = null
      }
    }
    if (bgImage) {
      bgImage.scale = new Vector2(0, 0)
    }

    if (skybox_mesh) skybox_mesh.setEnabled(true)
    if (floor_mesh) floor_mesh.setEnabled(true)
  }

  if (
    enable === true ||
    (enable !== false &&
      enable !== true &&
      (!backgroundHdrSkybox?.isEnabled() || (bgImage && bgImage.scale?.x <= 0)))
  ) {
    console.log('to enable')
    if (backgroundHdrSkybox) {
      backgroundHdrSkybox.setEnabled(true)
      const currentTexture =
        backgroundHdrSkybox.material?.getActiveTextures()[0]._texture?.url
      if (currentTexture) {
        const type = getTypeByFileName(currentTexture)
        scene.environmentTexture =
          type === EnvType.ENV || EnvType.DDS
            ? new CubeTexture(currentTexture, scene)
            : new HDRCubeTexture(currentTexture, scene, 128)
      }
    }
    if (bgImage) {
      bgImage.scale = new Vector2(1, 1)
    }

    if (skybox_mesh) skybox_mesh.setEnabled(false)
    if (floor_mesh) floor_mesh.setEnabled(false)
  }
}

export function togglePBR(active: boolean) {
  const modelMats = getModelMaterial()

  modelMats?.forEach(material => {
    material.environmentIntensity = active === true ? 1 : 0
  })
}

export function changeSkybox(filename: string, context = Context3D.get()) {
  return new Promise((resolve, reject) => {
    if (!context.scene) {
      reject(false)
      return
    }

    const reflectionTexture = new HDRCubeTexture(
      'https://brikl-poc.s3.ap-southeast-1.amazonaws.com/babylonjs/assets/env/' +
        filename,
      context.scene,
      128,
      false,
      true,
      false,
      true,
      () => {
        if (!context.scene) {
          reject(false)
          return
        }

        const backgroundSkybox = context.scene.getNodeByID('hdrSkyBox')

        if (backgroundSkybox) backgroundSkybox.dispose(false, true)

        const oldSkyBox = context.scene?.getMeshByName('BackgroundSkybox')
        if (oldSkyBox) {
          oldSkyBox.dispose(false, true)
        }
        const backgroundHelper =
          context.scene?.getMeshByName('BackgroundHelper')
        if (backgroundHelper) {
          backgroundHelper.dispose(false, true)
        }

        context.scene.createDefaultSkybox(reflectionTexture, true)
        resolve(true)
      }
    )
  })
}

export function changeVariant(variant: any) {
  console.log('changeVariant', variant)
}

// NEW FOR JSON CONFIGURATION

export function getMaterialByMeshName(
  meshesName: string[],
  modelId?: string,
  context = Context3D.get()
) {
  if (!context.scene) return

  let materials = meshesName.map(meshName => {
    const mesh = context.scene?.getMeshByName(meshName)
    if ((mesh && !modelId) || (mesh && mesh?.parent?.name === modelId)) {
      const material = mesh.material as PBRMaterial
      if (material) return material
    }
  })

  materials = _uniqBy(materials, 'name')
  console.log('materials', materials)

  return materials
}

export function changeColorByMeshName(
  color: IColor,
  meshesName: string[],
  modelId?: string,
  context = Context3D.get()
) {
  if (!context.scene) return

  const materials = getMaterialByMeshName(meshesName, modelId)

  materials?.forEach(material => {
    if (!material) return
    material.albedoColor = Color3.FromHexString(color.hex)

    changeMaterialStyle(material, color)

    material.albedoTexture?.dispose()
    material.metallicTexture?.dispose()
    material.bumpTexture?.dispose()
    // @ts-ignore
    material.albedoTexture = null
    // @ts-ignore
    material.metallicTexture = null
    // @ts-ignore
    material.bumpTexture = null
  })
}

export async function changeTextureByMeshName(
  textureAsset: ITexture,
  meshesName: string[],
  modelId?: string,
  customMaterialAttribute?: customMaterialAttribute,
  context = Context3D.get()
) {
  if (!context.scene) return

  let texture: Texture | undefined
  if (textureAsset.source.texture) {
    texture = await createTexture(
      textureAsset.source.texture,
      textureAsset.repeat
    )
  }

  let normalMapTexture: Texture | undefined
  if (textureAsset.source.normal) {
    normalMapTexture = await createTexture(
      textureAsset.source.normal,
      textureAsset.repeat
    )
  }

  const materials = getMaterialByMeshName(meshesName, modelId)

  materials?.forEach(material => {
    if (!material) return

    material.albedoTexture?.dispose()
    material.bumpTexture?.dispose()
    material.metallicTexture?.dispose()
    // @ts-ignore
    material.albedoTexture = null
    // @ts-ignore
    material.bumpTexture = null
    // @ts-ignore
    material.metallicTexture = null

    material.albedoColor = Color3.FromHexString('#FFFFFF') // Clear to white color before apply texture, if not color will overlap texture.
    if (texture) material.albedoTexture = texture
    material.roughness = textureAsset?.roughness || 0.5
    material.metallic = textureAsset?.metallic || 0.5

    if (normalMapTexture) {
      material.bumpTexture = normalMapTexture
      material.bumpTexture.level = 0.5
    }
  })
}

export function changeMaterialStyle(
  material: PBRMaterial,
  customMaterialAttribute?: IMaterialStyle
) {
  if (customMaterialAttribute?.alpha || customMaterialAttribute?.alpha === 0)
    material.alpha = customMaterialAttribute?.alpha
  if (
    customMaterialAttribute?.metallic ||
    customMaterialAttribute?.metallic === 0
  )
    material.metallic = customMaterialAttribute?.metallic
  if (
    customMaterialAttribute?.roughness ||
    customMaterialAttribute?.roughness === 0
  )
    material.roughness = customMaterialAttribute?.roughness

  if (customMaterialAttribute?.repeat) {
    const texture = material.albedoTexture as Texture
    texture.uScale = customMaterialAttribute?.repeat
    texture.vScale = customMaterialAttribute?.repeat
  }
}

export function switchingVariant(
  enableMeshName: string | boolean,
  variants: IVariant[],
  modelId?: string,
  context = Context3D.get()
) {
  if (!context.scene) return

  variants.forEach(variant => {
    let mesh: AbstractMesh | null = null

    if (modelId) {
      const parent = context.scene?.getMeshByName(modelId)
      const found = parent?.getChildren().find(c => c.name === variant.meshName)

      if (found) {
        mesh = found as AbstractMesh
      }
    } else {
      mesh = context.scene?.getMeshByName(variant.meshName) ?? null
    }

    if (mesh) mesh.setEnabled(enableMeshName === mesh.name)
  })
}

export function toggleMeshOutline(
  variants: IVariant[],
  enable: boolean,
  context = Context3D.get()
) {
  if (!context.scene) return

  variants.forEach(variant => {
    const mesh = context.scene?.getMeshByName(variant.meshName)
    if (mesh) {
      mesh.outlineColor = Color3.FromHexString('#0000FF')
      mesh.renderOutline = enable
    }
  })
}

// NEW FOR JSON CONFIGURATION

export function generateJSON() {
  // https://brikl-poc.s3.ap-southeast-1.amazonaws.com/babylonjs/models/chair-modular/Chair Modular Custom.gltf

  const GLTFUrl =
    'https://brikl-poc.s3.ap-southeast-1.amazonaws.com/babylonjs/models/chair-modular/Chair Modular Custom.gltf'
  var request = new XMLHttpRequest()
  request.open('GET', GLTFUrl, true)
  request.send(null)
  request.onreadystatechange = function () {
    if (request.readyState === 4 && request.status === 200) {
      var type = request.getResponseHeader('Content-Type')
      if (type?.indexOf('text') !== 1) {
        const jsonGLTF = JSON.parse(request.responseText) as GLTFType
        console.log('GLTF JSON', jsonGLTF)

        const _fileName = GLTFUrl.match(/[^\/\?#]+(?=[^\/]*$)/g)
        if (!_fileName?.length) {
          return
        }
        const fileName = _fileName[0]

        const meshGroupArray = []
        const meshes: IMesh[] = []
        jsonGLTF.nodes.forEach(node => {
          const extractName = node.name.split('-')
          if (extractName.length <= 1) return

          const variants: IVariant[] = []
          const meshGroup = extractName[0]

          // meshes.push({
          //   meshGroup: extractName[0],
          //   title: extractName[2],
          //   variants: []
          // })
        })

        const modelConfig: IModel = {
          id: fileName, // use filename for init
          title: fileName, // use filename for init
          source: GLTFUrl,
          meshes: [],
        }

        // return json;
      }
    }
  }
}
